import { useState } from "react";
import { useGetPromosQuery } from "@/services/api/promo/promoApi";

/**
 * Custom hook for managing promo data
 * Provides pagination and search functionality for promos
 */
export const usePromoData = () => {
  // ==========================================
  // Pagination State
  // ==========================================
  const [pagination, setPagination] = useState({
    page: 1,
    per_page: 10,
  });

  // ==========================================
  // Search State
  // ==========================================
  const [search, setSearch] = useState("");

  // ==========================================
  // Pagination Handlers
  // ==========================================
  const handlePageChange = (page) => {
    setPagination((prev) => ({ ...prev, page }));
  };

  const handlePageSizeChange = (per_page) => {
    setPagination({ page: 1, per_page });
  };

  // ==========================================
  // Search Handler
  // ==========================================
  const handleSearch = (searchTerm) => {
    const trimmedSearch = searchTerm.trim();
    setSearch(trimmedSearch);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const {
    data: apiResponse = { data: [], meta: {} },
    isLoading,
    isFetching,
    error,
    refetch,
  } = useGetPromosQuery({
    page: pagination.page,
    per_page: pagination.per_page,
    search: search,
  });

  // Extract data and meta from response
  const promosData = apiResponse.data || [];
  const metaData = apiResponse.meta || {};

  // Extract summary from meta statistics
  const summary = {
    total_promos: metaData?.statistics?.total || 0,
    total_used: metaData?.statistics?.used || 0,
    total_unused: metaData?.statistics?.unused || 0,
  };

  // Extract pagination info from meta
  const paginationInfo = {
    current_page: metaData?.current_page || 1,
    last_page: metaData?.last_page || 1,
    per_page: metaData?.per_page || 10,
    total: metaData?.total || 0,
  };

  return {
    // Data
    promosData,
    metaData,
    apiResponse,
    summary,

    // Pagination info
    paginationInfo,

    // Pagination handlers
    handlePageChange,
    handlePageSizeChange,

    // Search state and handler
    search,
    handleSearch,

    // Loading states
    isLoading,
    isFetching,

    // Error states
    error,
    isError: !!error,

    // Actions
    refetch,
  };
};
